import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  createPurchaseShipment,
  getPurchaseOrderInTransitOpen,
} from "@/shared/api/purchasing";
import { notify } from "@/shared/feedback/notify";

export function PurchaseShipmentNewPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const purchaseOrderId = searchParams.get("purchaseOrderId") ?? "";
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitOnCreate, setSubmitOnCreate] = useState(true);
  const [orderNumber, setOrderNumber] = useState("");
  const [lines, setLines] = useState<
    Array<{
      purchaseOrderLineId: string;
      productName: string;
      qtyRemaining: string;
      qty: string;
    }>
  >([]);

  useEffect(() => {
    if (!companySlug || !purchaseOrderId) return;
    let cancelled = false;
    getPurchaseOrderInTransitOpen(companySlug, purchaseOrderId)
      .then((res) => {
        if (cancelled) return;
        setOrderNumber(res.order.number);
        setLines(
          res.lines.map((l) => ({
            purchaseOrderLineId: l.purchaseOrderLineId,
            productName: l.productName,
            qtyRemaining: l.qtyRemaining,
            qty: l.qtyRemaining,
          })),
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, purchaseOrderId, navigate]);

  async function onCreate() {
    if (!companySlug || !purchaseOrderId) return;
    setBusy(true);
    setError(null);
    try {
      const payloadLines = lines
        .filter((l) => Number(l.qty) > 0)
        .map((l) => ({
          purchaseOrderLineId: l.purchaseOrderLineId,
          qty: l.qty,
        }));
      if (payloadLines.length === 0) {
        setError("Enter qty on at least one line");
        return;
      }
      const { shipment } = await createPurchaseShipment(companySlug, {
        purchaseOrderId,
        lines: payloadLines,
        submit: submitOnCreate,
      });
      notify.added(shipment.number);
      navigate(`/c/${companySlug}/purchasing/in-transit/${shipment.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setBusy(false);
    }
  }

  if (!companySlug) return null;

  return (
    <PageFrame width="narrow">
      <DeskDocBackLink
        listBasePath={`/c/${companySlug}/purchasing/in-transit`}
        listLabel="In transit"
      />
      <PageHeader
        eyebrow="In transit"
        title="New inbound shipment"
        description={
          purchaseOrderId
            ? `From ${orderNumber || "purchase order"} — logistics only; stock moves on goods receipt.`
            : "Open Create ▾ → In transit from a purchase order."
        }
      />
      {!purchaseOrderId ? (
        <Alert variant="destructive" className="mt-4">
          Missing purchaseOrderId.{" "}
          <Link to={`/c/${companySlug}/purchasing/orders`} className="underline">
            Pick an order
          </Link>
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      ) : null}
      {purchaseOrderId && lines.length === 0 && !error ? (
        <Alert variant="muted" className="mt-4">
          Nothing left to ship inbound (fully received or already in transit).
        </Alert>
      ) : null}
      {lines.length > 0 ? (
        <div className="mt-6 space-y-3">
          {lines.map((line) => (
            <div
              key={line.purchaseOrderLineId}
              className="flex flex-wrap items-end gap-3 border-b border-[var(--color-border)] pb-3"
            >
              <div className="min-w-[10rem] flex-1">
                <p className="m-0 text-sm font-medium">{line.productName}</p>
                <p className="m-0 text-xs text-[var(--color-muted)]">
                  Remaining {line.qtyRemaining}
                </p>
              </div>
              <div>
                <Label htmlFor={`qty-${line.purchaseOrderLineId}`}>Qty</Label>
                <Input
                  id={`qty-${line.purchaseOrderLineId}`}
                  className="mt-1 w-28 font-mono"
                  inputMode="decimal"
                  value={line.qty}
                  onChange={(e) =>
                    setLines((prev) =>
                      prev.map((l) =>
                        l.purchaseOrderLineId === line.purchaseOrderLineId
                          ? { ...l, qty: e.target.value }
                          : l,
                      ),
                    )
                  }
                />
              </div>
            </div>
          ))}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={submitOnCreate}
              onChange={(e) => setSubmitOnCreate(e.target.checked)}
            />
            Submit on create (open in transit)
          </label>
          <Button
            type="button"
            disabled={busy || !purchaseOrderId}
            pending={busy}
            onClick={() => void onCreate()}
          >
            {submitOnCreate ? "Create & submit" : "Save draft"}
          </Button>
        </div>
      ) : null}
    </PageFrame>
  );
}
